/**
 * Screen Manager for Poker Reborn
 *
 * Manages screen transitions and renders screens to the PixiJS stage.
 *
 * Container hierarchy:
 * - stage
 *   - loginScreen (isolated, shown when no credentials, has DOM overlay)
 *   - loadingScreen (isolated, handles own error UI)
 *   - appContainer (visible after auth)
 *     - screenContainer (Lobby, Table, etc.)
 *     - overlayContainer (ConnectionOverlay, future overlays)
 *
 * @see docs/systems/SCREEN_TRANSITIONS.md - Fade-to-black transition effect
 * @see docs/screens/login/README.md - Login screen documentation
 * @see tasks/active/T09_CONNECTION_LOSS_HANDLING_PLAN.md
 */

import { Application, Container, Graphics, Text } from 'pixi.js';
import { appState } from '@/state/AppState';
import { tableState } from '@/state/TableState';
import { networkService } from '@/services/NetworkService';
import { LoginScreenBase } from './LoginScreenBase';
import { createLoginScreen } from './LoginScreen';
import { LoadingScreen } from './LoadingScreen';
import { LobbyScreen, setPixiAppForRotation } from './LobbyScreen';
import { TableScreen } from './TableScreen';
import { SettingsScreen } from './SettingsScreen';
import { ConnectionOverlay } from '@/components/common/overlays/ConnectionOverlay';
import { FullscreenSuggestionOverlay } from '@/components/common/overlays/FullscreenSuggestionOverlay';
import { isFullscreen } from '@/utils/fullscreen';
import { Button } from '@/components/common/Button';
import { navigateToScreen } from '@/state/AppState';
import { log } from '@/utils/log';
import { easeInCubic, easeOutQuart } from '@/effects/utils/easing';
import type { ScreenType } from '@/types/ui';
import type { ConnectionDiagnostic } from '@/types/network';

// =============================================================================
// MODULE-LEVEL INSTANCE (for rotation state access from other screens)
// =============================================================================

let screenManagerInstance: ScreenManager | null = null;

/**
 * Get the ScreenManager instance (for rotation state access)
 */
export function getScreenManager(): ScreenManager | null {
  return screenManagerInstance;
}

// =============================================================================
// SCREEN MANAGER
// =============================================================================

export class ScreenManager {
  private app: Application;

  // Container hierarchy (see docstring at top of file)
  private loginScreen: LoginScreenBase | null = null;
  private loadingScreen: LoadingScreen | null = null;
  private appContainer: Container;
  private screenContainer: Container;
  private overlayContainer: Container;
  private connectionOverlay: ConnectionOverlay;
  private fullscreenSuggestion: FullscreenSuggestionOverlay;
  private hasShownFullscreenSuggestion = false;

  private currentScreen: Container | null = null;
  private currentScreenType: ScreenType | null = null;
  private resizeObserver: ResizeObserver | null = null;

  // Screen instances (lazy loaded, excludes loading screen)
  private screens: Partial<Record<ScreenType, Container>> = {};

  // Track last close code to distinguish intentional vs unexpected disconnects
  private lastCloseCode: number | null = null;

  // Global rotation state (0 or 90 degrees)
  private currentRotation: number = 0;

  // Fade transition state
  private isTransitioning = false;
  private transitionRafId: number | null = null;

  constructor(app: Application) {
    this.app = app;

    // Store instance for module-level access
    screenManagerInstance = this;

    // Set app reference for rotation demo
    setPixiAppForRotation(app);

    // Create loading screen (isolated, direct child of stage)
    const { width, height } = this.getScreenSize();
    this.loadingScreen = new LoadingScreen(width, height);
    this.app.stage.addChild(this.loadingScreen);

    // Create app container (visible after auth, contains screens + overlays)
    this.appContainer = new Container();
    this.appContainer.visible = false; // Hidden until auth
    this.app.stage.addChild(this.appContainer);

    // Create screen container (inside appContainer)
    this.screenContainer = new Container();
    this.appContainer.addChild(this.screenContainer);

    // Create overlay container (above screens, inside appContainer)
    this.overlayContainer = new Container();
    this.appContainer.addChild(this.overlayContainer);

    // Create connection overlay
    this.connectionOverlay = new ConnectionOverlay();
    this.connectionOverlay.resize(width, height);
    this.overlayContainer.addChild(this.connectionOverlay);

    // Create fullscreen suggestion overlay (shown on small screens)
    this.fullscreenSuggestion = new FullscreenSuggestionOverlay();
    this.fullscreenSuggestion.resize(width, height);
    this.overlayContainer.addChild(this.fullscreenSuggestion);

    // Subscribe to screen changes
    appState.currentScreen.subscribe((screen) => {
      this.showScreen(screen);
    });

    // Setup network event handlers for connection overlay
    this.setupConnectionOverlayHandlers();

    // Handle resize using ResizeObserver (catches dev tools open/close)
    const canvas = this.app.canvas;
    if (canvas.parentElement) {
      this.resizeObserver = new ResizeObserver(() => {
        // Small delay to let PixiJS update its internal dimensions first
        requestAnimationFrame(() => {
          this.handleResize();
        });
      });
      this.resizeObserver.observe(canvas.parentElement);
    }

    // Fallback to window resize for edge cases
    window.addEventListener('resize', () => {
      this.handleResize();
    });

    // Background tab recovery - detect when tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.handleTabVisible();
      }
    });
  }

  /**
   * Show a screen
   *
   * Login and Loading screens are handled specially - they're isolated from appContainer.
   * When showing login/loading: respective screen visible, appContainer hidden
   * When showing others: login/loading hidden, appContainer visible
   */
  showScreen(screenType: ScreenType): void {
    if (screenType === this.currentScreenType) return;
    if (this.isTransitioning) return;

    // Handle login screen separately (it's isolated, has DOM elements)
    if (screenType === 'login') {
      // Create login screen if not exists
      if (!this.loginScreen) {
        const { width, height } = this.getScreenSize();
        this.loginScreen = createLoginScreen(width, height);
        this.app.stage.addChildAt(this.loginScreen, 0); // Behind loading screen
      }

      // Show login screen, hide others
      this.loginScreen.visible = true;
      if (this.loadingScreen) {
        this.loadingScreen.visible = false;
      }
      this.appContainer.visible = false;

      // Hide current app screen
      if (this.currentScreen && this.currentScreen !== this.loginScreen) {
        this.currentScreen.visible = false;
      }

      this.currentScreen = this.loginScreen;
      this.currentScreenType = 'login';
      return;
    }

    // Handle loading screen separately (it's isolated)
    if (screenType === 'loading') {
      // Show loading screen, hide login and app container
      if (this.loadingScreen) {
        this.loadingScreen.visible = true;
      }
      if (this.loginScreen) {
        this.loginScreen.visible = false;
        this.loginScreen.cleanup(); // Remove DOM elements
      }
      this.appContainer.visible = false;

      // Hide current app screen
      if (this.currentScreen) {
        this.currentScreen.visible = false;
      }

      this.currentScreen = this.loadingScreen;
      this.currentScreenType = 'loading';
      return;
    }

    // Transitioning away from login - cleanup DOM elements
    if (this.currentScreenType === 'login' && this.loginScreen) {
      this.loginScreen.visible = false;
      this.loginScreen.cleanup();
    }

    // Transitioning away from loading - use fade effect
    if (this.currentScreenType === 'loading' && this.loadingScreen) {
      this.performLoadingFade(screenType);
      return;
    }

    // Hide current app screen
    if (this.currentScreen && this.currentScreen !== this.loadingScreen) {
      this.currentScreen.visible = false;
    }

    this.activateScreen(screenType);
  }

  /**
   * Get screen dimensions
   */
  getScreenSize(): { width: number; height: number } {
    return {
      width: this.app.screen.width,
      height: this.app.screen.height,
    };
  }

  /**
   * Get a screen instance by type (creates if not exists)
   */
  getScreen<T extends Container>(screenType: ScreenType): T | undefined {
    if (!this.screens[screenType]) {
      const screen = this.createScreen(screenType);
      if (screen) {
        this.screens[screenType] = screen;
        this.screenContainer.addChild(screen);
      }
    }
    return this.screens[screenType] as T | undefined;
  }

  /**
   * Get the login screen instance
   */
  getLoginScreen(): LoginScreenBase | undefined {
    return this.loginScreen ?? undefined;
  }

  /**
   * Get the loading screen instance
   */
  getLoadingScreen(): LoadingScreen | undefined {
    return this.loadingScreen ?? undefined;
  }

  /**
   * Get the connection overlay instance
   */
  getConnectionOverlay(): ConnectionOverlay {
    return this.connectionOverlay;
  }

  /**
   * Get the current rotation angle (0 or 90)
   */
  getRotation(): number {
    return this.currentRotation;
  }

  /**
   * Set the rotation angle (called by LobbyScreen when user rotates)
   */
  setRotation(angle: number): void {
    this.currentRotation = angle;
  }

  // ===========================================================================
  // SCREEN TRANSITION (FADE)
  // ===========================================================================

  /**
   * Activate a screen: create if needed, make visible, resize, refresh.
   * Extracted from showScreen() for reuse by fade transition.
   */
  private activateScreen(screenType: ScreenType): void {
    let screen = this.screens[screenType];

    if (!screen) {
      screen = this.createScreen(screenType);
      if (screen) {
        this.screens[screenType] = screen;
        this.screenContainer.addChild(screen);
      }
    }

    if (screen) {
      screen.visible = true;
      this.currentScreen = screen;
      this.currentScreenType = screenType;

      if ('resize' in screen && typeof screen.resize === 'function') {
        const { width, height } = this.getScreenSize();
        (screen as { resize: (w: number, h: number) => void }).resize(width, height);
      }

      if ('refresh' in screen && typeof screen.refresh === 'function') {
        (screen as { refresh: () => void }).refresh();
      }

      this.maybeShowFullscreenSuggestion();
    }
  }

  /**
   * Animate a container's alpha from startAlpha to endAlpha over durationMs.
   */
  private animateAlpha(
    target: Container,
    startAlpha: number,
    endAlpha: number,
    durationMs: number,
    easingFn: (t: number) => number,
  ): Promise<void> {
    return new Promise((resolve) => {
      const startTime = performance.now();
      target.alpha = startAlpha;

      const tick = (): void => {
        const elapsed = performance.now() - startTime;
        const rawProgress = Math.min(elapsed / durationMs, 1);
        const easedProgress = easingFn(rawProgress);

        target.alpha = startAlpha + (endAlpha - startAlpha) * easedProgress;

        if (rawProgress < 1) {
          this.transitionRafId = requestAnimationFrame(tick);
        } else {
          target.alpha = endAlpha;
          this.transitionRafId = null;
          resolve();
        }
      };

      this.transitionRafId = requestAnimationFrame(tick);
    });
  }

  /**
   * Fade from loading screen to target screen via black, then iris-in reveal.
   * Phase 1: Loading alpha 1 → 0 (250ms, easeInCubic)
   * Phase 2: Iris-in circular reveal on appContainer (500ms, easeOutQuart)
   */
  private performLoadingFade(screenType: ScreenType): void {
    if (!this.loadingScreen) return;

    this.isTransitioning = true;

    // Phase 1: Fade out loading screen
    this.animateAlpha(this.loadingScreen, 1, 0, 250, easeInCubic)
      .then(() => {
        // Swap screens at the black frame
        if (this.loadingScreen) {
          this.loadingScreen.visible = false;
          this.loadingScreen.alpha = 1; // Reset for potential reuse
        }
        this.appContainer.visible = true;
        this.appContainer.alpha = 0;

        // Activate target screen (content ready behind mask)
        this.activateScreen(screenType);

        // Brief pause on black — let the moment breathe
        return new Promise<void>((resolve) => setTimeout(resolve, 150));
      })
      .then(() => {
        // Phase 2: Iris-in reveal + simultaneous alpha fade
        return this.animateIrisIn(this.appContainer, 600, easeOutQuart);
      })
      .then(() => {
        this.isTransitioning = false;
      })
      .catch(() => {
        // Fallback: ensure screens are in correct state
        this.isTransitioning = false;
        if (this.loadingScreen) {
          this.loadingScreen.visible = false;
          this.loadingScreen.alpha = 1;
        }
        this.appContainer.visible = true;
        this.appContainer.alpha = 1;
        this.appContainer.mask = null;
      });
  }

  /**
   * Animate an iris-in (expanding circle) reveal on a container.
   * Combines a circular mask with a simultaneous alpha fade for a soft,
   * semi-transparent reveal — less harsh than a hard circle edge.
   */
  private animateIrisIn(
    target: Container,
    durationMs: number,
    easingFn: (t: number) => number,
  ): Promise<void> {
    const { width, height } = this.getScreenSize();
    const centerX = width / 2;
    const centerY = height / 2;
    // Max radius = half the diagonal, ensures circle covers all corners
    const maxRadius = Math.sqrt(centerX * centerX + centerY * centerY);

    const irisMask = new Graphics();
    target.addChild(irisMask);
    target.mask = irisMask;

    return new Promise((resolve) => {
      const startTime = performance.now();

      const tick = (): void => {
        const elapsed = performance.now() - startTime;
        const rawProgress = Math.min(elapsed / durationMs, 1);
        const easedProgress = easingFn(rawProgress);
        const currentRadius = maxRadius * easedProgress;

        // Alpha ramps up faster than the circle (reaches 1 at ~60% of duration)
        // This softens the hard circle edge — content is translucent early on
        const alphaProgress = Math.min(rawProgress / 0.6, 1);
        target.alpha = easingFn(alphaProgress);

        irisMask.clear();
        irisMask.circle(centerX, centerY, currentRadius);
        irisMask.fill(0xffffff);

        if (rawProgress < 1) {
          this.transitionRafId = requestAnimationFrame(tick);
        } else {
          // Clean up: remove mask so container renders normally
          target.mask = null;
          target.alpha = 1;
          target.removeChild(irisMask);
          irisMask.destroy();
          this.transitionRafId = null;
          resolve();
        }
      };

      this.transitionRafId = requestAnimationFrame(tick);
    });
  }

  /**
   * Create a screen instance
   *
   * Note: Loading screen is created in constructor, not here.
   */
  private createScreen(screenType: ScreenType): Container | undefined {
    const { width, height } = this.getScreenSize();

    switch (screenType) {
      case 'login':
        // Login screen is created in showScreen when needed
        return this.loginScreen ?? undefined;

      case 'loading':
        // Loading screen is created in constructor
        return this.loadingScreen ?? undefined;

      case 'lobby':
        return new LobbyScreen(width, height);

      case 'table':
        return new TableScreen(width, height);

      case 'settings':
        return new SettingsScreen(width, height);

      case 'shop':
        return this.createPlaceholderScreen('Shop - Coming Soon!', 'lobby');

      case 'profile':
        return this.createPlaceholderScreen('Profile - Coming Soon!', 'lobby');

      default:
        log.ui.warning`Unknown screen type: ${screenType}`;
        return undefined;
    }
  }

  /**
   * Create a placeholder screen for unimplemented screens
   */
  private createPlaceholderScreen(
    message: string,
    backTo?: ScreenType
  ): Container {
    const { width, height } = this.getScreenSize();
    const container = new Container();
    const padding = 20;

    // Background
    const bg = new Graphics();
    bg.rect(0, 0, width, height);
    bg.fill(0x1a1a2e);
    container.addChild(bg);

    // Back button (if backTo specified)
    if (backTo) {
      const backButton = new Button({
        label: '← Back',
        width: 100,
        height: 36,
        style: {
          backgroundColor: 0x2a3a4a,
          hoverColor: 0x3a4a5a,
          pressColor: 0x1a2a3a,
          borderColor: 0x4a5a6a,
          fontSize: 14,
        },
      });
      backButton.x = padding;
      backButton.y = padding;
      backButton.onClick = () => { navigateToScreen(backTo); };
      container.addChild(backButton);
    }

    // Message
    const text = new Text({
      text: message,
      style: {
        fill: 0xffffff,
        fontSize: 24,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    text.anchor.set(0.5);
    text.x = width / 2;
    text.y = height / 2;
    container.addChild(text);

    return container;
  }

  /**
   * Handle window resize
   */
  private handleResize(): void {
    const { width, height } = this.getScreenSize();

    // Resize login screen
    if (this.loginScreen) {
      this.loginScreen.resize(width, height);
    }

    // Resize loading screen
    if (this.loadingScreen) {
      this.loadingScreen.resize(width, height);
    }

    // Resize connection overlay
    this.connectionOverlay.resize(width, height);

    // Resize fullscreen suggestion overlay
    this.fullscreenSuggestion.resize(width, height);

    // Resize all created app screens
    Object.values(this.screens).forEach((screen) => {
      if (screen && 'resize' in screen && typeof screen.resize === 'function') {
        (screen as { resize: (w: number, h: number) => void }).resize(width, height);
      }
    });
  }

  /**
   * Handle tab becoming visible after being backgrounded.
   * Forces re-render, layout refresh, and screen refresh to fix stale UI.
   *
   * @see docs/systems/BACKGROUND_TAB_RECOVERY.md
   */
  private handleTabVisible(): void {
    log.app.debug`Tab visible - triggering recovery`;

    // 1. Force PixiJS to render immediately
    const pixiApp = (window as unknown as { pokerApp: Application | undefined }).pokerApp;
    if (pixiApp?.renderer) {
      pixiApp.renderer.render(pixiApp.stage);
    }

    // 2. Trigger resize to recalculate all layouts
    // Use RAF to batch with any pending updates
    requestAnimationFrame(() => {
      this.handleResize();

      // 3. Refresh current screen to re-sync visual state with game state
      // This handles components like opponent cards that need explicit refresh
      if (this.currentScreen && 'refresh' in this.currentScreen && typeof this.currentScreen.refresh === 'function') {
        (this.currentScreen as { refresh: () => void }).refresh();
      }
    });
  }

  // ===========================================================================
  // FULLSCREEN SUGGESTION
  // ===========================================================================

  /**
   * Show fullscreen suggestion on small screens (< 400px).
   * Only triggers once per session, only on lobby/table screens.
   */
  private maybeShowFullscreenSuggestion(): void {
    if (this.hasShownFullscreenSuggestion) return;
    if (isFullscreen()) return;

    const { width } = this.getScreenSize();
    if (width >= 400) return;

    // Only suggest on game screens (lobby or table)
    if (this.currentScreenType !== 'lobby' && this.currentScreenType !== 'table') return;

    this.hasShownFullscreenSuggestion = true;
    this.fullscreenSuggestion.show();
  }

  // ===========================================================================
  // CONNECTION OVERLAY HANDLERS
  // ===========================================================================

  /**
   * Setup network event handlers for ConnectionOverlay
   *
   * The overlay shows during connection loss when NOT on loading screen.
   * Loading screen has its own error handling via showDiagnostic().
   *
   * @see tasks/active/T09_CONNECTION_LOSS_HANDLING_PLAN.md
   */
  private setupConnectionOverlayHandlers(): void {
    // Track close code to detect intentional disconnects
    networkService.on('disconnected', ({ code }) => {
      this.lastCloseCode = code;

      // Skip overlay for:
      // 1. Loading screen (has its own error UI)
      // 2. Intentional disconnect (code 1000)
      if (this.currentScreenType === 'loading' || code === 1000) {
        return;
      }

      // Show reconnecting overlay with initial attempt
      this.connectionOverlay.showReconnecting(1, this.getReconnectDelay(1), () => {
        this.retryConnection();
      });
      this.updateSeatReservedVisibility();
    });

    // Update overlay on each reconnection attempt
    networkService.on('reconnecting', ({ attempt, delay }) => {
      // Skip if on loading screen
      if (this.currentScreenType === 'loading') {
        return;
      }

      this.connectionOverlay.showReconnecting(attempt, delay, () => {
        this.retryConnection();
      });
      this.updateSeatReservedVisibility();
    });

    // Show success state on reconnection
    networkService.on('reconnected', () => {
      // Only show if overlay is currently visible
      if (this.connectionOverlay.getState() !== 'hidden') {
        this.connectionOverlay.showReconnected();
      }
    });

    // Show failure state with diagnostic
    networkService.on('connectionDiagnostic', (diagnostic: ConnectionDiagnostic) => {
      // Skip if on loading screen (LoadingScreen handles its own diagnostics)
      if (this.currentScreenType === 'loading') {
        return;
      }

      // Only show if we were already showing the overlay (reconnection failed)
      // or if this is a fresh disconnect that wasn't intentional
      if (
        this.connectionOverlay.getState() !== 'hidden' ||
        (this.lastCloseCode !== null && this.lastCloseCode !== 1000)
      ) {
        this.connectionOverlay.showFailed(diagnostic, () => {
          // Retry: trigger a new connection attempt
          this.retryConnection();
        });
      }
    });
  }

  /**
   * Update "seat reserved" message visibility based on current table state
   */
  private updateSeatReservedVisibility(): void {
    const isSeated = tableState.isSeated.get();
    this.connectionOverlay.setSeatReservedVisible(isSeated);
  }

  /**
   * Get reconnect delay for a given attempt (exponential backoff)
   */
  private getReconnectDelay(attempt: number): number {
    // Match the logic in NetworkService: 1s base, 2x exponential, 30s max
    const baseDelay = 1000;
    const maxDelay = 30000;
    return Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
  }

  /**
   * Trigger a new connection attempt immediately
   */
  private retryConnection(): void {
    log.network.info`Retry connection requested from overlay`;
    networkService.reconnectNow();
  }
}
